import { useEffect, useState } from "react";
import { NavLink, Outlet, Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Package, ShoppingBag, Users, Settings, ChevronLeft,
  FileText, Sliders, Sparkles, FolderTree, Truck, Share2, Mail, Menu, X,
} from "lucide-react";

const LINKS = [
  { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/products", label: "Productos", icon: Package },
  { to: "/admin/categories", label: "Categorías", icon: FolderTree },
  { to: "/admin/orders", label: "Pedidos", icon: ShoppingBag },
  { to: "/admin/customers", label: "Clientes", icon: Users },
  { to: "/admin/carriers", label: "Transportistas", icon: Truck },
  { to: "/admin/emails", label: "Emails IA", icon: Mail },
  { to: "/admin/social", label: "Redes sociales", icon: Share2 },
  { to: "/admin/pages", label: "Páginas CMS", icon: FileText },
  { to: "/admin/site-config", label: "Editor Home", icon: Sliders },
  { to: "/admin/post-generator", label: "Posts IA", icon: Sparkles },
  { to: "/admin/settings", label: "Configuración", icon: Settings },
];

const LOGO = "https://customer-assets.emergentagent.com/job_prestige-omni-store/artifacts/8poujyff_mhlumora%20logo.jpg";

function NavItems({ onNavigate }) {
  return (
    <nav className="p-4 flex-1 space-y-1 overflow-y-auto">
      {LINKS.map(l => (
        <NavLink
          key={l.to}
          to={l.to}
          end={l.end}
          onClick={onNavigate}
          data-testid={`admin-nav-${l.label.toLowerCase()}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
              isActive
                ? "bg-carbon text-gold border-l-2 border-gold"
                : "text-ivory/70 hover:text-ivory hover:bg-carbon/50 border-l-2 border-transparent"
            }`
          }
        >
          <l.icon size={16} />
          <span className="uppercase tracking-wider text-xs">{l.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

export default function AdminLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Close drawer on route change & lock body scroll while open
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  const currentLabel = LINKS.find(l => (l.end ? location.pathname === l.to : location.pathname.startsWith(l.to)))?.label || "Admin";

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-carbon lg:flex">
      {/* ===== Desktop sidebar (lg+) ===== */}
      <aside className="hidden lg:flex w-64 bg-graphite border-r border-smoke flex-col fixed h-screen z-30">
        <div className="p-6 border-b border-smoke">
          <Link to="/" className="flex items-center gap-2 text-ivory/60 hover:text-gold text-xs uppercase tracking-[0.2em]">
            <ChevronLeft size={14} /> Volver al sitio
          </Link>
          <img src={LOGO} alt="M&H Lumora" className="h-12 w-auto object-contain mb-4 mt-4" style={{ mixBlendMode: "screen" }} />
          <p className="text-xs uppercase tracking-[0.3em] text-ivory/40">Admin</p>
        </div>
        <NavItems />
      </aside>

      {/* ===== Mobile top bar (< lg) ===== */}
      <header className="lg:hidden sticky top-0 z-40 bg-graphite border-b border-smoke flex items-center justify-between px-4 py-3">
        <button
          onClick={() => setDrawerOpen(true)}
          className="p-2 text-ivory hover:text-gold"
          data-testid="admin-menu-toggle"
          aria-label="Abrir menú"
        >
          <Menu size={22} />
        </button>
        <div className="flex items-center gap-3 flex-1 justify-center">
          <img src={LOGO} alt="M&H Lumora" className="h-7 w-auto object-contain" style={{ mixBlendMode: "screen" }} />
          <span className="text-[10px] uppercase tracking-[0.3em] text-gold border-l border-smoke pl-3">{currentLabel}</span>
        </div>
        <Link to="/" className="p-2 text-ivory/60 hover:text-gold" aria-label="Volver al sitio">
          <ChevronLeft size={20} />
        </Link>
      </header>

      {/* ===== Mobile drawer (slide-in) ===== */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="admin-drawer">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-carbon/80 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
          />
          {/* Panel */}
          <aside className="relative w-72 max-w-[85vw] bg-graphite border-r border-smoke flex flex-col animate-slide-in h-full">
            <div className="p-5 border-b border-smoke flex items-start justify-between">
              <div>
                <Link to="/" onClick={() => setDrawerOpen(false)} className="flex items-center gap-2 text-ivory/60 hover:text-gold text-[10px] uppercase tracking-[0.2em]">
                  <ChevronLeft size={12} /> Volver al sitio
                </Link>
                <img src={LOGO} alt="M&H Lumora" className="h-10 w-auto object-contain mt-3 mb-2" style={{ mixBlendMode: "screen" }} />
                <p className="text-[10px] uppercase tracking-[0.3em] text-ivory/40">Admin</p>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                className="p-2 text-ivory/70 hover:text-gold"
                data-testid="admin-drawer-close"
                aria-label="Cerrar menú"
              >
                <X size={20} />
              </button>
            </div>
            <NavItems onNavigate={() => setDrawerOpen(false)} />
          </aside>
        </div>
      )}

      {/* ===== Main content ===== */}
      <main className="flex-1 lg:ml-64 p-4 sm:p-6 lg:p-10 min-w-0">
        <div className="max-w-full">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
